import { createSelector } from '@ngrx/store';
import { AnnouncementEntitiesState } from './announcement-entities.state';
import { announcementEntitiesAdapter } from './announcement-entities.adapter';
import { selectEntities } from '../entities';

/**
 * Selectors für Announcement-Entities.
 */
export class AnnouncementEntitiesSelectors {
  private static readonly adapterSelectors = announcementEntitiesAdapter.getSelectors();

  private static readonly _feature = createSelector(
    selectEntities,
    (state) => state.announcementEntities,
  );

  /** Alle Announcements als Array. */
  static readonly announcements = createSelector(
    this._feature,
    this.adapterSelectors.selectAll,
  );

  /** Announcements als Dictionary (ID → Entity). */
  static readonly announcementEntities = createSelector(
    this._feature,
    this.adapterSelectors.selectEntities,
  );

  /** Alle Announcement-IDs. */
  static readonly announcementIds = createSelector(
    this._feature,
    this.adapterSelectors.selectIds,
  );

  /**
   * Einzelnes Announcement per ID.
   * @param id - Die ID des Announcements
   */
  static announcementById(id: string) {
    return createSelector(
      this.announcementEntities,
      (entities) => entities[id] ?? null,
    );
  }

  /** Statistiken. */
  static readonly statistics = createSelector(
    this._feature,
    (state) => state.statistics,
  );

  /** Gesamtanzahl der Announcements. */
  static readonly totalElements = createSelector(
    this._feature,
    (state) => state.totalElements,
  );

  /** Ladezustand für die Liste. */
  static readonly isLoadingList = createSelector(
    this._feature,
    (state) => state.isLoadingList,
  );

  /** Gibt an, ob die Liste mindestens einmal geladen wurde. */
  static readonly isListLoaded = createSelector(
    this._feature,
    (state) => state.isListLoaded,
  );

  /** Ladezustand für die Statistiken. */
  static readonly isLoadingStatistics = createSelector(
    this._feature,
    (state) => state.isLoadingStatistics,
  );

  /** Gibt an, ob die Statistiken mindestens einmal geladen wurden. */
  static readonly isStatisticsLoaded = createSelector(
    this._feature,
    (state) => state.isStatisticsLoaded,
  );

  /** IDs der Announcements, die gerade gelöscht werden. */
  static readonly deletingIds = createSelector(
    this._feature,
    (state) => state.deletingIds,
  );

  /** IDs der Announcements, die gerade veröffentlicht/zurückgezogen werden. */
  static readonly publishingIds = createSelector(
    this._feature,
    (state) => state.publishingIds,
  );

  /**
   * Prüft, ob ein bestimmtes Announcement gerade gelöscht wird.
   * @param id - Die ID des Announcements
   */
  static isDeleting(id: string) {
    return createSelector(
      this.deletingIds,
      (deletingIds) => deletingIds.includes(id),
    );
  }

  /**
   * Prüft, ob ein bestimmtes Announcement gerade veröffentlicht/zurückgezogen wird.
   * @param id - Die ID des Announcements
   */
  static isPublishing(id: string) {
    return createSelector(
      this.publishingIds,
      (publishingIds) => publishingIds.includes(id),
    );
  }

  /** Prüft, ob gerade mindestens ein Delete-Vorgang läuft. */
  static readonly isAnyDeleting = createSelector(
    this.deletingIds,
    (deletingIds) => deletingIds.length > 0,
  );

  /** Prüft, ob gerade mindestens ein Publish/Unpublish-Vorgang läuft. */
  static readonly isAnyPublishing = createSelector(
    this.publishingIds,
    (publishingIds) => publishingIds.length > 0,
  );

  /** Fehlermeldung beim Laden der Liste. */
  static readonly listError = createSelector(
    this._feature,
    (state) => state.listError,
  );

  /** Fehlermeldung beim Laden der Statistiken. */
  static readonly statisticsError = createSelector(
    this._feature,
    (state) => state.statisticsError,
  );

  /** Kombinierter Ladezustand (Liste oder Statistiken werden geladen). */
  static readonly isLoading = createSelector(
    this._feature,
    (state) => state.isLoadingList || state.isLoadingStatistics,
  );
}
